import { Input, Separator } from '@/_components/ui'
import { ContentContainer } from '@/features/_components/content-container'
import { InputGroup } from '@/features/_components/input-group'
import { queries } from '@/features/_lib/react-query'
import type { TExposedApplicationDTO } from '@doutorsim/shared/applications'
import type { TExposedPersonOfficialAddressDTO } from '@doutorsim/shared/exposed/persons'
import { centsToReais, formatBRLFromReais } from '@doutorsim/shared/helpers/currency'
import type { TExposedPersonAggregateDTO } from '@doutorsim/shared/persons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

interface ConfirmedDeclarationsCardProps {
  application: TExposedApplicationDTO
}

// Post-confirmation read from the person record. "Was this confirmed" is
// inferred from the application status machine having advanced past
// pending-declarations-confirmation; prior to that, persons still holds the
// create-time nulls.
export const ConfirmedDeclarationsCard = ({ application }: ConfirmedDeclarationsCardProps) => {
  const { data: person } = useQuery({
    ...queries.staffPersons.detail(application.companyId, application.personId),
    enabled: !!application.companyId && !!application.personId,
  })

  const terminalStatuses: TExposedApplicationDTO['status'][] = [
    'pending-signature',
    'pending-disbursement',
    'active',
    'settled',
    'rejected',
    'cancelled',
    'expired',
  ]
  if (!person) return null
  if (!terminalStatuses.includes(application.status)) return null

  return (
    <ContentContainer>
      <div className="flex items-center justify-between">
        <h2 className="font-bold">Dados confirmados</h2>
      </div>

      <InputGroup>
        <ReadonlyField label="Nascimento" value={formatBirthDate(person.birthDate)} />
        <ReadonlyField label="Nome da mãe" value={person.motherName ?? ''} />
      </InputGroup>
      <InputGroup>
        <ReadonlyField label="Renda mensal" value={formatIncome(person.estimatedIncome)} />
        <ReadonlyField label="Estado" value={addressValue(person.address, 'subdivisionId')} />
      </InputGroup>

      <Separator className="bg-brand-grayscale-3" />

      <InputGroup>
        <ReadonlyField label="CEP" value={formatCep(person.address?.postcode ?? '')} />
        <ReadonlyField label="Logradouro" value={addressValue(person.address, 'thoroughfare')} />
      </InputGroup>
      <InputGroup>
        <ReadonlyField label="Número" value={addressValue(person.address, 'premiseNumber')} />
        <ReadonlyField label="Complemento" value={addressValue(person.address, 'subPremise')} />
      </InputGroup>
      <InputGroup>
        <ReadonlyField label="Bairro" value={addressValue(person.address, 'premise')} />
        <ReadonlyField label="Cidade" value={addressValue(person.address, 'locality')} />
      </InputGroup>
    </ContentContainer>
  )
}

interface ReadonlyFieldProps {
  label: string
  value: string
}

const ReadonlyField = ({ label, value }: ReadonlyFieldProps) => (
  <div className="w-1/2 space-y-2">
    <label className="text-sm font-medium">{label}</label>
    <Input value={value} disabled readOnly />
  </div>
)

const formatBirthDate = (value: Date | string | null): string => {
  if (!value) return ''
  const d = dayjs(value)
  return d.isValid() ? d.format('DD/MM/YYYY') : ''
}

const formatIncome = (cents: number | null): string => {
  if (cents == null) return ''
  return formatBRLFromReais(centsToReais(cents))
}

const formatCep = (value: string): string => {
  const digits = value.replace(/\D/g, '')
  return digits.length === 8 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits
}

const addressValue = (
  address: TExposedPersonAggregateDTO['address'],
  field: keyof TExposedPersonOfficialAddressDTO,
): string => {
  if (!address) return ''
  const value = address[field as keyof typeof address]
  return typeof value === 'string' ? value : ''
}
